/**
 * Realtime hooks for chat — mirror the proven postgres_changes pattern from
 * src/admin/useAdminRealtime.js. RLS on `messages` ensures a client only
 * receives rows for chats it belongs to.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../../supabaseClient";

/**
 * Subscribe to INSERTs on a single chat's messages.
 * @param {string} chatId
 * @param {(message) => void} onMessage  called with the new row
 */
export function useChatThreadRealtime(chatId, onMessage) {
  const cbRef = useRef(onMessage);
  cbRef.current = onMessage;

  useEffect(() => {
    if (!chatId) return undefined;
    const ch = supabase
      .channel(`chat-${chatId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `chat_id=eq.${chatId}` },
        (payload) => cbRef.current?.(payload.new)
      )
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [chatId]);
}

/** Subscribe to read-state (last_read_at) changes for a chat → live ✓✓. */
export function useReadReceiptsRealtime(chatId, onChange) {
  const cbRef = useRef(onChange);
  cbRef.current = onChange;
  useEffect(() => {
    if (!chatId) return undefined;
    const ch = supabase
      .channel(`reads-${chatId}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "chat_members", filter: `chat_id=eq.${chatId}` }, () => cbRef.current?.())
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [chatId]);
}

/**
 * Subscribe to ALL message inserts the user can see (RLS-filtered to their
 * chats). Fires `onMessage(newRow)` so a chats list / global toast can react.
 * A 90s backup poll catches silently-dropped channels (same as admin).
 */
export function useChatsListRealtime(onMessage) {
  const cbRef = useRef(onMessage);
  cbRef.current = onMessage;
  // Unique channel per hook instance — two consumers (global toast + chats
  // list) must not share a topic or one silently drops.
  const topicRef = useRef(`chats-list-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    const ch = supabase
      .channel(topicRef.current)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, (payload) => cbRef.current?.(payload.new))
      .subscribe();
    const poll = setInterval(() => cbRef.current?.(null), 90000);
    return () => {
      clearInterval(poll);
      supabase.removeChannel(ch);
    };
  }, []);
}

/**
 * Presence + typing for a chat. Returns the set of online user ids, a map of
 * who's currently typing, and a throttled `setTyping()` to broadcast my own.
 */
export function useChatPresence(chatId, me, myName) {
  const [onlineIds, setOnlineIds] = useState(() => new Set());
  const [typingUsers, setTypingUsers] = useState({});
  const chanRef = useRef(null);
  const lastSent = useRef(0);

  useEffect(() => {
    if (!chatId || !me) return undefined;
    const ch = supabase.channel(`presence-${chatId}`, { config: { presence: { key: me } } });
    ch.on("presence", { event: "sync" }, () => setOnlineIds(new Set(Object.keys(ch.presenceState()))));
    ch.on("broadcast", { event: "typing" }, ({ payload }) => {
      if (!payload || payload.user_id === me) return;
      setTypingUsers((prev) => ({ ...prev, [payload.user_id]: { name: payload.name, ts: Date.now() } }));
    });
    ch.subscribe(async (status) => {
      if (status === "SUBSCRIBED") await ch.track({ user_id: me, online_at: new Date().toISOString() });
    });
    chanRef.current = ch;
    const prune = setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now(); const next = {}; let changed = false;
        for (const [k, v] of Object.entries(prev)) { if (now - v.ts < 3500) next[k] = v; else changed = true; }
        return changed ? next : prev;
      });
    }, 1000);
    return () => { clearInterval(prune); supabase.removeChannel(ch); chanRef.current = null; };
  }, [chatId, me]);

  const setTyping = useCallback(() => {
    const ch = chanRef.current; if (!ch) return;
    const now = Date.now();
    if (now - lastSent.current < 1500) return; // throttle broadcasts
    lastSent.current = now;
    ch.send({ type: "broadcast", event: "typing", payload: { user_id: me, name: myName } });
  }, [me, myName]);

  return { onlineIds, typingUsers, setTyping };
}
